/**
 * SearchPanel - Search messages + attachments (fulltext + hashtag).
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { chatSearchApi } from '../../services/chatApi';
import { useChat } from '../../contexts/ChatContext';
import type { ChatSearchResult } from '../../types/chat';

interface SearchPanelProps {
  onClose: () => void;
}

const SearchPanel: React.FC<SearchPanelProps> = ({ onClose }) => {
  const { t } = useTranslation('chat');
  const { channels, setCurrentChannel } = useChat();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ChatSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const data = query.startsWith('#')
        ? await chatSearchApi.hashtag(query.slice(1), includeArchived)
        : await chatSearchApi.fulltext(query, undefined, includeArchived);
      setResults(data);
    } catch (e) {
      console.error('Search failed:', e);
    } finally {
      setLoading(false);
    }
  }, [query, includeArchived]);

  const handleResultClick = useCallback((result: ChatSearchResult) => {
    const channel = channels.find(c => c.id === result.channel_id);
    if (channel) {
      setCurrentChannel(channel);
      onClose();
    }
  }, [channels, setCurrentChannel, onClose]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="font-semibold text-sm">{t('search')}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="px-4 py-3 border-b">
        <div className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder={t('searchPlaceholder')}
            className="flex-1 border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSearch}
            className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
          >
            {t('search')}
          </button>
        </div>
        <label className="flex items-center gap-2 mt-2 text-xs text-gray-500">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={e => setIncludeArchived(e.target.checked)}
            className="rounded"
          />
          {t('includeArchived')}
        </label>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : results.length > 0 ? (
          results.map(result => (
            <button
              key={result.id}
              onClick={() => handleResultClick(result)}
              className="w-full text-left px-4 py-3 border-b hover:bg-gray-50"
            >
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                <span className="font-medium">#{result.channel_name}</span>
                <span>{new Date(result.created_at).toLocaleDateString()}</span>
                {result.source === 'attachment' && (
                  <span className="bg-gray-100 px-1.5 py-0.5 rounded">{t('attachment')}</span>
                )}
              </div>
              <div className="text-sm text-gray-800 line-clamp-2">{result.content}</div>
            </button>
          ))
        ) : query && !loading ? (
          <div className="text-center text-gray-400 text-sm py-8">
            {t('noResults')}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SearchPanel;
